/* eslint-disable camelcase */
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useSelector } from "react-redux";
import styled, { keyframes } from "styled-components";
import { transferPlayback } from "../api/SpotifyController";
import { getFromSessionStorage, setToSessionStorage } from "../../../hooks/useSessionStorage";
import AlbumPhoto from "../../../assets/images/spotify/noAlbumImage.png";
import UI_Logo_Spotify_Full from "../assets/UI_Logo_Spotify_Full.png";
import minimize from "../assets/icons/minimize-rounded.png";

import Volume from "../assets/icons/Volume.svg";
import { ReactComponent as PrevIcon } from "../assets/icons/1/left-chevron-arrow-round-outline-icon.svg";
import { ReactComponent as NextIcon } from "../assets/icons/1/right-chevron-arrow-round-outline-icon.svg";
import { ReactComponent as PlayIcon } from "../assets/icons/1/play-button-round-icon.svg";
import { ReactComponent as PauseIcon } from "../assets/icons/1/pause-button-round-icon.svg";
import { ReactComponent as FFwdIcon } from "../assets/icons/1/forward-button-icon.svg";
import { ReactComponent as BackFrdIcon } from "../assets/icons/1/backward-button-icon.svg";
import { ReactComponent as LoopIcon } from "../assets/icons/1/music-player-repeat-round-outline-icon.svg";
import { ReactComponent as ShuffleIcon } from "../assets/icons/1/music-player-shuffle-round-outline-icon.svg";
import VolumeSlider from "./VolumeSlider";
import MusicPlayerProgressBar from "./MusicPlayerProgressBar";
import Loop_Active from "../assets/icons/Loop_Active.png";
import Shuffle_Active from "../assets/icons/Shuffle_Active.png";
import Hex from "../assets/icons/Hex.png";
import X from "../assets/icons/x.svg";
import { getWindowSize, getDeviceOrientation } from "../../../utils/generalUtils";
import { socket } from "../../../socket";

const track = {
    uri: "",
    name: "",
    album: {
        images: [{ url: "" }],
        name: "",
    },
    artists: [{ name: "" }],
};

function SpotifyControls(props) {
    const [minimized, setMinimized] = useState(false);
    // Drag state for minimized modal
    const [dragPos, setDragPos] = useState({ x: window.innerWidth - 320 - 20, y: 120 }); // default right:20px, top:120px
    const [dragging, setDragging] = useState(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    // Drag handlers for minimized modal
    const handleDragStart = (e) => {
        if (!minimized) return;
        setDragging(true);
        const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
        const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
        dragOffset.current = {
            x: clientX - dragPos.x,
            y: clientY - dragPos.y,
        };
        document.body.style.userSelect = "none";
    };

    const handleDrag = (e) => {
        if (!dragging) return;
        const clientX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
        const clientY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;
        setDragPos({
            x: Math.max(0, Math.min(window.innerWidth - 320, clientX - dragOffset.current.x)),
            y: Math.max(0, Math.min(window.innerHeight - 70, clientY - dragOffset.current.y)),
        });
    };

    const handleDragEnd = () => {
        setDragging(false);
        document.body.style.userSelect = "";
    };

    useEffect(() => {
        if (!dragging) return;
        window.addEventListener("mousemove", handleDrag);
        window.addEventListener("mouseup", handleDragEnd);
        window.addEventListener("touchmove", handleDrag);
        window.addEventListener("touchend", handleDragEnd);
        return () => {
            window.removeEventListener("mousemove", handleDrag);
            window.removeEventListener("mouseup", handleDragEnd);
            window.removeEventListener("touchmove", handleDrag);
            window.removeEventListener("touchend", handleDragEnd);
        };
    }, [dragging]);
    const userId = useSelector((state) => state.user.userId);
    const [spotifyToken, setSpotifyToken] = useState(getFromSessionStorage("spotifyAccessToken"));
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [deviceId, setDeviceId] = useState(null);
    const [spotifyDisconnected, setSpotifyDisconnected] = useState(false);
    const [openVolume, setOpenVolume] = useState(false);
    const volumeBtnRef = useRef(null);
    const volumeSliderRef = useRef(null);
    // Close volume slider when clicking outside
    useEffect(() => {
        if (!openVolume) return;
        function handleClickOutside(e) {
            if (
                volumeBtnRef.current &&
                volumeSliderRef.current &&
                !volumeBtnRef.current.contains(e.target) &&
                !volumeSliderRef.current.contains(e.target)
            ) {
                setOpenVolume(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [openVolume]);
    const [volumeSliderPos, setVolumeSliderPos] = useState({ top: 0, left: 0 });
    const [shuffleActive, setShuffleActive] = useState(false);
    const [loopActive, setLoopActive] = useState(false);
    const [ispaused, setPaused] = useState(false);
    const [currentTrack, setTrack] = useState(track);
    const [volume, setVolume] = useState(0.25);
    const playerRef = useRef(null);
    const playerInitialized = useRef(false);
    const [windowSize, setWindowSize] = useState(getWindowSize());
    const [orientation, setOrientation] = useState(getDeviceOrientation());
    const roomSettings = JSON.parse(localStorage.getItem("roomSettings")) || {};
    const roomTrackRef = useRef(roomSettings?.spotifyTrack || null);

    // Socket listener for Spotify token updates
    useEffect(() => {
        const handleSpotifyToken = (data) => {
            if (data.userId === userId) {
                setToSessionStorage("spotifyAccessToken", data.accessToken);
                setSpotifyToken(data.accessToken);
                setSpotifyDisconnected(false);
            }
        };

        socket.on("connect", () => console.log("Connected", socket.id));
        socket.on("spotifyToken", handleSpotifyToken);

        return () => socket.off("spotifyToken", handleSpotifyToken);
    }, [userId]);

    const openSpotifyLoginPopup = () => {
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const redirectUri = encodeURIComponent(`${process.env.REACT_APP_CORE_API_URI}/web/spotify/callback/popup`);
        const url = `${process.env.REACT_APP_CORE_API_URI}/web/spotify/login/popup/${redirectUri}?userId=${encodeURIComponent(userId)}`;
        const popup = window.open(
            url,
            "SpotifyLogin",
            `width=${width},height=${height},left=${left},top=${top},resizable,scrollbars`
        );
        if (popup) popup.focus();
    };

    useEffect(() => {
        if (!spotifyToken || !props.openSpotify || playerInitialized.current) return;

        const script = document.createElement("script");
        script.src = "https://sdk.scdn.co/spotify-player.js";
        script.async = true;
        document.body.appendChild(script);

        window.onSpotifyWebPlaybackSDKReady = () => {
            const player = new window.Spotify.Player({
                name: `Intraverse Player`,
                getOAuthToken: (cb) => cb(spotifyToken),
                volume,
            });
            playerRef.current = player;

            player.addListener("ready", async ({ device_id }) => {
                console.log("Player ready with device_id:", device_id);
                setDeviceId(device_id);
                playerInitialized.current = true;

                // Only transfer playback if this is the first initialization
                await transferPlayback(device_id, spotifyToken);
            });

            player.addListener("player_state_changed", (state) => {
                if (!state) return;
                setCurrentTime(state.position / 1000);
                setDuration(state.duration / 1000);
                setPaused(state.paused);
                if (state.track_window?.current_track?.uri !== currentTrack.uri) {
                    setTrack(state.track_window.current_track);
                }
            });

            player.connect();
        };
    }, [spotifyToken, props.openSpotify]);

    useEffect(() => {
        if (!playerRef.current) return;
        if (!props.openSpotify) {
            playerRef.current.pause().catch(console.error);
        }
    }, [props.openSpotify]);

    useEffect(() => {
        if (!deviceId || !spotifyToken || !props.openSpotify) return;
        const roomTrackUri = roomSettings?.spotifyTrack;

        // Only trigger play if roomTrack has changed
        if (roomTrackUri && roomTrackUri !== roomTrackRef.current) {
            const isTrack = roomTrackUri.startsWith("spotify:track:");
            fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${spotifyToken}` },
                body: JSON.stringify({
                    [isTrack ? "uris" : "context_uri"]: isTrack ? [roomTrackUri] : roomTrackUri,
                }),
            }).catch(console.error);

            roomTrackRef.current = roomTrackUri; // update last played room track
        } else {
            console.log("no room track set.");
        }
    }, [roomSettings?.spotifyTrack, deviceId, spotifyToken, props.openSpotify]);

    useEffect(() => {
        if (playerRef.current) playerRef.current.setVolume(volume).catch(console.error);
    }, [volume]);

    useEffect(() => {
        const handleResize = () => {
            setWindowSize(getWindowSize());
            setOrientation(getDeviceOrientation());
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // ====== CONTROLS ======
    const handleSpotifyPause = async () => {
        if (!playerRef.current) return;
        const state = await playerRef.current.getCurrentState();
        if (!state) return;
        if (state.paused) await playerRef.current.resume();
        else await playerRef.current.pause();
    };

    const handleSpotifyNext = async () => {
        if (!playerRef.current) return;
        try {
            await playerRef.current.nextTrack();
        } catch (err) {
            console.error("Next track failed:", err);
        }
    };

    const handleSpotifyPrevious = async () => {
        if (!playerRef.current) return;
        try {
            await playerRef.current.previousTrack();
        } catch (err) {
            console.error("Previous track failed:", err);
        }
    };

    const handleSpotifyShuffle = async () => {
        if (!deviceId) return;
        await fetch(`https://api.spotify.com/v1/me/player/shuffle?state=${!shuffleActive}&device_id=${deviceId}`, {
            method: "PUT",
            headers: { Authorization: `Bearer ${spotifyToken}` },
        }).catch(console.error);
        setShuffleActive((prev) => !prev);
    };

    const handleSpotifyLoop = async () => {
        if (!deviceId) return;
        await fetch(
            `https://api.spotify.com/v1/me/player/repeat?state=${loopActive ? "off" : "track"}&device_id=${deviceId}`,
            {
                method: "PUT",
                headers: { Authorization: `Bearer ${spotifyToken}` },
            }
        ).catch(console.error);
        setLoopActive((prev) => !prev);
    };

    const handleFastForward = async (sec = 10) => {
        if (!playerRef.current) return;
        try {
            const state = await playerRef.current.getCurrentState();
            if (!state) return;
            let newPos = state.position + sec * 1000;
            if (newPos > state.duration) newPos = state.duration;
            await playerRef.current.seek(newPos);
            setCurrentTime(newPos / 1000);
        } catch (err) {
            console.error("Fast forward failed:", err);
        }
    };

    const handleBackFrwd = async (sec = 10) => {
        if (!playerRef.current) return;
        try {
            const state = await playerRef.current.getCurrentState();
            if (!state) return;
            let newPos = state.position - sec * 1000;
            if (newPos < 0) newPos = 0;
            await playerRef.current.seek(newPos);
            setCurrentTime(newPos / 1000);
        } catch (err) {
            console.error("Rewind failed:", err);
        }
    };

    return (
        <>
            {props.openSpotify && spotifyToken && (
                <SpotifyContainer
                    style={{
                        display: props.openSpotify ? "flex" : "none",
                        ...(minimized
                            ? {
                                  left: dragPos.x,
                                  top: dragPos.y,
                                  right: "auto",
                                  bottom: "auto",
                                  cursor: "grab",
                                  transition: dragging ? "none" : "box-shadow 0.2s",
                              }
                            : {}),
                    }}
                    windowSize={windowSize}
                    orientation={orientation}
                    minimized={minimized}
                    onMouseDown={minimized ? handleDragStart : undefined}
                    onTouchStart={minimized ? handleDragStart : undefined}
                >
                    <LogoContainer>
                        <SpotifyLogo src={UI_Logo_Spotify_Full} />
                        <IconContainer>
                            <VolumeButtonWrapper>
                                <VolumeButton
                                    ref={volumeBtnRef}
                                    src={Volume}
                                    alt="Volume"
                                    onClick={() => {
                                        if (!openVolume && volumeBtnRef.current) {
                                            const rect = volumeBtnRef.current.getBoundingClientRect();
                                            setVolumeSliderPos({
                                                top: rect.bottom + 4, // 4px margin below button
                                                left: rect.left + rect.width / 2,
                                            });
                                        }
                                        setOpenVolume((prev) => !prev);
                                    }}
                                    openVolume={openVolume}
                                />
                                {openVolume &&
                                    ReactDOM.createPortal(
                                        <div
                                            ref={volumeSliderRef}
                                            style={{
                                                position: "fixed",
                                                top: volumeSliderPos.top,
                                                left: volumeSliderPos.left,
                                                zIndex: 2000,
                                                transform: "translateX(-50%)",
                                            }}
                                        >
                                            <VolumeSlider volume={volume} setVolume={setVolume} />
                                        </div>,
                                        document.body
                                    )}
                            </VolumeButtonWrapper>
                            <MinmizeIcon
                                style={{ paddingBottom: "8px" }}
                                src={minimize}
                                onClick={() => setMinimized((m) => !m)}
                                alt="Exit Icon"
                            />
                            <MinmizeIcon src={X} onClick={() => props.setOpenSpotify(false)} />
                        </IconContainer>
                    </LogoContainer>
                    {!minimized && (
                        <>
                            <TrackContainer>
                                <div>
                                    <AlbumImagePlayer
                                        src={currentTrack?.album?.images[0]?.url || AlbumPhoto}
                                        alt="Album Image"
                                    />
                                </div>
                                <TextContainer>
                                    <SongTitle>{currentTrack?.name || "Loading song title..."}</SongTitle>
                                    <ArtistName>{currentTrack?.artists[0]?.name || "Loading..."}</ArtistName>
                                </TextContainer>
                            </TrackContainer>
                            <MusicPlayerProgressBar duration={duration} currentTime={currentTime} />
                            <ControlContainer>
                                <StyledShuffle isActive={shuffleActive} onClick={handleSpotifyShuffle} />
                                <StyledBFrwd onClick={() => handleBackFrwd(10)} />
                                <StyledPrevious onClick={handleSpotifyPrevious} />
                                {ispaused ? (
                                    <StyledPlay onClick={handleSpotifyPause} />
                                ) : (
                                    <StyledPause onClick={handleSpotifyPause} />
                                )}
                                <StyledNext onClick={handleSpotifyNext} />
                                <StyledFastForward onClick={() => handleFastForward(10)} />
                                <StyledLoop isActive={loopActive} onClick={handleSpotifyLoop} />
                            </ControlContainer>
                        </>
                    )}
                </SpotifyContainer>
            )}
            {props.openSpotify && !spotifyToken && (
                <SpotifyDisconnectContainer onClick={openSpotifyLoginPopup}>
                    <ExitIcon
                        src={X}
                        onClick={(e) => {
                            e.stopPropagation();
                            props.setOpenSpotify(false);
                        }}
                        alt="Exit Icon"
                    />
                    <SpotifyLogo src={UI_Logo_Spotify_Full} width="40%" />
                    <DiscModalText>
                        Tap here to connect your <strong>Spotify Premium</strong>!
                    </DiscModalText>
                </SpotifyDisconnectContainer>
            )}
        </>
    );
}

export default SpotifyControls;

const SpotifyContainer = styled.div`
    /* Minimized styles */
    ${(props) =>
        props.minimized
            ? `
        width: 300px !important;
        height: 70px !important;
        min-width: 300px !important;
        min-height: 70px !important;
        max-width: 180px !important;
        max-height: 70px !important;
        padding: 12px !important;
        flex-direction: row !important;
        align-items: center !important;
        justify-content: flex-end !important;
        overflow: hidden !important;
        background: rgba(14, 5, 23, 0.7) !important;
        right: 140px !important;
    `
            : ""}
    /* width: 450px;
    height: 300px; */
    position: fixed;
    padding: 20px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background: rgba(14, 5, 23, 0.9);
    border: 4px solid rgba(190, 174, 255, 0.2);
    border-radius: 10px;
    z-index: 800;
    flex-wrap: nowrap;
    overflow: hidden;
    pointer-events: auto;

    ${(props) => {
        if (props.orientation === "portrait") {
            return `
        transform: rotate(-90deg);
        height: 250px;
        width: 300px;
        top: 4vw;
        right: 10vh;
      `;
        }
        return `
            transform: none;
            width: 380px;
            height: 280px;
            top: 135px;
            right: 150px;
          `;
    }}

    @media (max-width: 846px) {
        top: 80px;
        right: 20px;
        height: 220px;
        width: 300px;
    }
`;

const VolumeButtonWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const SpotifyDisconnectContainer = styled.div`
    width: 450px;
    height: 300px;
    position: fixed;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(14, 5, 23, 0.9);
    border: 4px solid rgba(190, 174, 255, 0.2);
    border-radius: 10px;
    z-index: 1000;
    top: 150px;
    right: 150px;
    flex-wrap: nowrap; /* Prevent unintended wrapping */
    overflow: hidden; /* Avoid overflow issues */
    cursor: pointer;
`;

const LogoContainer = styled.div`
    width: 100%;
    height: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const TrackContainer = styled.div`
    width: 100%;
    height: auto;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    flex-direction: row;
`;

const ControlContainer = styled.div`
    width: 100%;
    height: auto;
    display: flex;
    flex-direction: row;
    justify-content: space-evenly;
    align-items: center;
`;

const DiscModalText = styled.p`
    color: #ced1d9;
    text-align: center;
    font-family: "Barlow", sans-serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    margin-top: 20px;

    & > strong {
        color: #fbfcff;
        font-weight: 700;
    }
`;

const SpotifyLogo = styled.img`
    height: ${(props) => (props.width ? props.width : "25px")};
    height: ${(props) => (props.width ? "auto" : "25px")};
`;
const MinmizeIcon = styled.img`
    height: 35px;
    width: 35px;
    margin-left: 10px;
    padding: 1px 1px 1px 1px;
    background-image: url(${minimize});
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 5px;
`;
const IconContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    z-index: 100000;
    overflow: visible;
    position: relative;
`;

// VolumeSliderWrapper is not needed when using portal

const AlbumImagePlayer = styled.img`
    height: 75px;
    margin-right: 25px;
    border-radius: 10px;
    border: 2px solid var(--purple-primary-20, rgba(190, 174, 255, 0.2));
`;

const TextContainer = styled.div`
    flex: 1; /* Allow the container to take up remaining space */
    display: flex;
    flex-direction: column;
    min-width: 0; /* Ensure it works with text-overflow: ellipsis */
`;

const SongTitle = styled.h1`
    overflow: hidden;
    color: #fbfcff;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: "Barlow", sans-serif;
    font-size: 19px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-edge: cap;
    line-height: 130%;
`;

const ArtistName = styled.p`
    color: #9da0a7;
    font-family: "Barlow", sans-serif;
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
    margin-top: 5px;
    line-height: 130%;
`;

const HexImageRight = styled.img`
    position: absolute;
    bottom: 18px;
    right: 74px;
    height: 6px;
    cursor: pointer;
`;

const HexImageLeft = styled.img`
    position: absolute;
    bottom: 18px;
    left: 75px;
    height: 6px;
    cursor: pointer;
`;

const StyledNext = styled(NextIcon)`
    height: 30px;
    cursor: pointer;
    fill: #9da0a7;
    &:hover {
        fill: #beaeff;
    }
`;

const StyledPlay = styled(PlayIcon)`
    height: 30px;
    cursor: pointer;
    fill: #9da0a7;
    &:hover {
        fill: #beaeff;
    }
`;
const StyledPause = styled(PauseIcon)`
    height: 30px;
    cursor: pointer;
    fill: #9da0a7;
    &:hover {
        fill: #beaeff;
    }
`;

const StyledPrevious = styled(PrevIcon)`
    height: 30px;
    cursor: pointer;
    fill: #9da0a7;
    &:hover {
        fill: #beaeff;
    }
`;

const StyledFastForward = styled(FFwdIcon)`
    height: 30px;
    cursor: pointer;
    fill: #9da0a7;
    &:hover {
        fill: #beaeff;
    }
`;

const StyledBFrwd = styled(BackFrdIcon)`
    height: 30px;
    cursor: pointer;
    fill: #9da0a7;
    &:hover {
        fill: #beaeff;
    }
`;

const StyledLoop = styled(LoopIcon)`
    height: 30px;
    cursor: pointer;
    fill: ${(props) => (props.isActive ? "#beaeff" : "#9da0a7")};
    &:hover {
        fill: #beaeff;
    }
`;

const StyledShuffle = styled(ShuffleIcon)`
    height: 30px;
    cursor: pointer;
    fill: ${(props) => (props.isActive ? "#beaeff" : "#9da0a7")};

    &:hover {
        fill: #beaeff;
    }
`;

const VolumeButton = styled.img`
    height: 36px;
    cursor: pointer;

    ${(props) => {
        if (props.openVolume) {
            return `
                border: 2.133px solid var(--Purple-Primary, #beaeff);
                border-radius: 5.333px;`;
        }
    }}
`;

const ExitIcon = styled.img`
    position: absolute;
    top: 10px;
    right: 10px;
    height: 60px;
    cursor: pointer;
    pointer-events: auto;
    z-index: 1200;
`;
